import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createTag,
  createTagMapping,
  deleteTagMapping,
  listTagMappings,
  listTags,
} from "../services/tagsApi";
import "../styles/EntityTagsField.css";

function norm(v) {
  return String(v || "").trim().toLowerCase();
}

export default function EntityTagsField({
  moduleName,
  entityId,
  moduleScope,
  title = "Tags",
  canEdit = true,
  draftTagIds = [],
  onDraftTagIdsChange = null,
}) {
  const scope = moduleScope || moduleName;
  const isDraftMode = !entityId && typeof onDraftTagIdsChange === "function";
  const [catalog, setCatalog] = useState([]);
  const [mapped, setMapped] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [err, setErr] = useState("");

  const loadCatalog = useCallback(async () => {
    try {
      const rows = await listTags({});
      setCatalog(Array.isArray(rows) ? rows : []);
    } catch {
      setCatalog([]);
    }
  }, []);

  const loadMapped = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setErr("");
    try {
      const rows = await listTagMappings({ moduleName, entityId });
      setMapped(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(e.message || "Failed to load tags");
      setMapped([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, moduleName]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { if (!isDraftMode) loadMapped(); }, [isDraftMode, loadMapped]);

  const draftMapped = useMemo(() => {
    if (!isDraftMode) return [];
    const byId = new Map(catalog.map((t) => [String(t.id), t]));
    return (draftTagIds || []).map((id) => byId.get(String(id))).filter(Boolean);
  }, [catalog, draftTagIds, isDraftMode]);

  const effectiveMapped = isDraftMode ? draftMapped : mapped;

  const mappedKeys = useMemo(() => new Set(effectiveMapped.map((m) => norm(m.tag_name))), [effectiveMapped]);

  const filteredOptions = useMemo(
    () => suggestions.filter((t) => !mappedKeys.has(norm(t.tag_name))).slice(0, 8),
    [suggestions, mappedKeys]
  );

  const exactTag = useMemo(
    () => catalog.find((t) => norm(t.tag_name) === norm(query)),
    [catalog, query]
  );

  const addExistingById = async (targetId) => {
    if (!targetId) return;
    if (isDraftMode) {
      const next = Array.from(new Set([...(draftTagIds || []), targetId]));
      onDraftTagIdsChange(next);
      setQuery("");
      setSuggestions([]);
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await createTagMapping({
        module_name: moduleName,
        entity_id: entityId,
        tag_id: targetId,
      });
      await loadMapped();
      setQuery("");
      setSuggestions([]);
    } catch (e) {
      setErr(e.message || "Failed to add tag");
    } finally {
      setSaving(false);
    }
  };

  const createAndAdd = async (name) => {
    setSaving(true);
    setErr("");
    try {
      const created = await createTag({ tag_name: name, module_scope: scope });
      const createdTagId = created?.tag?.id;
      if (!createdTagId) throw new Error("Tag created but id missing");
      await addExistingById(createdTagId);
      await loadCatalog();
    } catch (e) {
      // If creation failed due to existing case-insensitive tag, reuse it.
      if (e?.status === 409) {
        const rows = await listTags({ search: name });
        const exact = (Array.isArray(rows) ? rows : []).find((t) => norm(t.tag_name) === norm(name));
        if (exact?.id) {
          await addExistingById(exact.id);
          return;
        }
      }
      setErr(e.message || "Failed to create tag");
    } finally {
      setSaving(false);
    }
  };

  const addTagFromInput = async (raw) => {
    const name = String(raw || "").trim();
    if (!name) return;
    if (mappedKeys.has(norm(name))) {
      setQuery("");
      setSuggestions([]);
      return;
    }
    setErr("");
    const exact = exactTag || filteredOptions.find((t) => norm(t.tag_name) === norm(name));
    if (exact?.id) {
      await addExistingById(exact.id);
      return;
    }
    await createAndAdd(name);
  };

  const removeDraftTag = (tagId) => {
    if (!isDraftMode) return;
    const target = String(tagId);
    onDraftTagIdsChange((draftTagIds || []).filter((id) => String(id) !== target));
  };

  const removeMappedTag = async (mappingId) => {
    if (isDraftMode || !entityId) return;
    setSaving(true);
    setErr("");
    try {
      await deleteTagMapping(mappingId);
      await loadMapped();
    } catch (e) {
      setErr(e.message || "Failed to remove tag");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const q = String(query || "").trim();
    if (!q) {
      setSuggestions([]);
      return;
    }
    let active = true;
    setLoadingSuggest(true);
    listTags({ search: q })
      .then((rows) => {
        if (!active) return;
        setSuggestions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!active) return;
        setSuggestions([]);
      })
      .finally(() => {
        if (!active) return;
        setLoadingSuggest(false);
      });
    return () => { active = false; };
  }, [query]);

  return (
    <section className="cmn-tags-wrap">
      <div className="cmn-tags-head">
        <span className="cmn-tags-title">{title}</span>
        <span className="cmn-tags-count">{effectiveMapped.length}</span>
      </div>

      {!entityId && !isDraftMode ? (
        <div className="cmn-tags-hint">Create the release first, then add tags.</div>
      ) : (
        <>
          {loading && !isDraftMode ? (
            <div className="cmn-tags-hint">Loading tags…</div>
          ) : effectiveMapped.length === 0 ? (
            <div className="cmn-tags-hint">No tags mapped yet.</div>
          ) : (
            <div className="cmn-tags-list">
              {effectiveMapped.map((m) => (
                <span key={`${m.tag_id || m.id}-${m.id || m.tag_name}`} className="cmn-tag-chip">
                  {m.tag_name}
                  {isDraftMode && (
                    <button
                      type="button"
                      className="cmn-tag-chip-x"
                      onClick={() => removeDraftTag(m.id || m.tag_id)}
                      aria-label={`Remove ${m.tag_name}`}
                    >
                      ×
                    </button>
                  )}
                  {!isDraftMode && canEdit && (
                    <button
                      type="button"
                      className="cmn-tag-chip-x"
                      onClick={() => removeMappedTag(m.id)}
                      aria-label={`Remove ${m.tag_name}`}
                      disabled={saving}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="cmn-tags-editor">
              <input
                className="cmn-tags-input"
                placeholder="Type to search, then Enter to add…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setErr("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addTagFromInput(query);
                  }
                }}
              />
              {loadingSuggest && <div className="cmn-tags-hint">Searching tags…</div>}
              {query && filteredOptions.length > 0 && (
                <div className="cmn-tags-suggest">
                  {filteredOptions.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="cmn-tags-opt"
                      onClick={() => addExistingById(t.id)}
                    >
                      {t.tag_name}
                    </button>
                  ))}
                </div>
              )}
              <div className="cmn-tags-hint">
                {saving ? "Updating tags…" : "Select from suggestions, or type a new tag and press Enter."}
              </div>
            </div>
          )}

          {err && <div className="cmn-tags-err">{err}</div>}
        </>
      )}
    </section>
  );
}
